import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import { unitsApi, UNITS_FILTER_OPTIONS_KEY } from "api/units";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { getErrorMessage } from "utils/error";
import { UnitsFilters } from "./types";

// Each bucket is counted server-side against the other applied filters, so the
// drawer reopens showing options that match what the grid is already showing.
const appliedFilterParams = (filters: UnitsFilters) => ({
  states: filters.states.length ? filters.states : undefined,
  cities: filters.cities.length ? filters.cities : undefined,
  property_group_ids: filters.property_group_ids.length
    ? filters.property_group_ids
    : undefined,
  property_ids: filters.property_ids.length ? filters.property_ids : undefined,
  unit_ids: filters.unit_ids.length ? filters.unit_ids : undefined,
  owner_ids: filters.owner_ids.length ? filters.owner_ids : undefined,
  sources: filters.sources.length ? filters.sources : undefined,
  current_statuses: filters.current_statuses.length
    ? filters.current_statuses
    : undefined,
  needs_lockbox: filters.needs_lockbox || undefined,
  no_lockbox: filters.no_lockbox || undefined
});

export const useUnitsFilterOptions = ({
  enabled,
  liveOnListings,
  filters
}: {
  enabled: boolean;
  liveOnListings: boolean;
  filters: UnitsFilters;
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const appliedParams = appliedFilterParams(filters);

  const { data, isFetching } = useQuery({
    queryKey: [
      UNITS_FILTER_OPTIONS_KEY,
      debouncedSearch,
      liveOnListings,
      appliedParams
    ],
    queryFn: () =>
      unitsApi.getFilterOptions({
        search: debouncedSearch || undefined,
        live_on_listings: liveOnListings || undefined,
        ...appliedParams
      }),
    enabled,
    placeholderData: keepPreviousData,
    meta: { onError: (error: unknown) => toast.error(getErrorMessage(error)) }
  });

  return { filterOptions: data, isFetching, setSearch };
};
